import { useSyncExternalStore } from "react";

// Live per-video download progress (0..1), deliberately OUTSIDE mobileVideoStore.
//
// Progress ticks arrive up to ~10×/second per active download. If they flowed
// through mobileVideoStore, every commit would hand a new state object to
// EVERY subscriber — bookmarks, playlist, the whole funder workspace — and
// re-render them, even though none of them care about a download fraction.
// That is what froze the Round Overview (a 2,700-line panel re-rendering ~10×/s
// during a download). Keeping progress here means it is structurally
// impossible for a non-progress consumer to re-render on a tick: the durable
// store never commits for progress, and only the two download-progress UIs
// (DownloadAllControl, DownloadDetails) subscribe to this channel.
//
// In-memory only: progress is ephemeral and reconstructed on reconcile, so it
// never needs to persist.
class DownloadProgressStore {
  private readonly values = new Map<string, number>();
  // Bumped on any change, so an aggregate consumer (the "download all" bar)
  // can subscribe to "something changed" without a per-id snapshot.
  private version = 0;
  private readonly listeners = new Set<() => void>();

  get(id: string): number {
    return this.values.get(id) ?? 0;
  }

  getVersion(): number {
    return this.version;
  }

  set(id: string, value: number): void {
    if (this.values.get(id) === value) {
      return;
    }

    this.values.set(id, value);
    this.version += 1;
    this.emit();
  }

  // Zero a whole handoff's fractions with ONE notification. Per-id `set` calls
  // would wake the progress UIs once per video, and each wake recomputes the
  // round's byte totals across every video — quadratic work in the same frame
  // as the handoff that queued them.
  reset(ids: readonly string[], options: { remove?: boolean } = {}): void {
    let changed = false;

    for (const id of ids) {
      if (options.remove === true) {
        if (this.values.delete(id)) {
          changed = true;
        }

        continue;
      }

      if (this.values.get(id) === 0) {
        continue;
      }

      this.values.set(id, 0);
      changed = true;
    }

    if (!changed) {
      return;
    }

    this.version += 1;
    this.emit();
  }

  clear(id: string): void {
    if (!this.values.delete(id)) {
      return;
    }

    this.version += 1;
    this.emit();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const downloadProgressStore = new DownloadProgressStore();

// Per-video progress. The snapshot is the video's own fraction, so a component
// using this only re-renders when THAT video's progress changes — a tick for a
// different video is a no-op (useSyncExternalStore bails on an unchanged
// number).
export function useDownloadProgress(videoId: string): number {
  return useSyncExternalStore(
    (listener) => downloadProgressStore.subscribe(listener),
    () => downloadProgressStore.get(videoId),
    () => 0,
  );
}

// For an aggregate consumer that needs many videos' progress at once (the
// "download all" byte bar): re-render on any progress change, then read
// `downloadProgressStore.get(id)` per video. Returns the version counter so the
// snapshot is a stable primitive.
export function useDownloadProgressVersion(): number {
  return useSyncExternalStore(
    (listener) => downloadProgressStore.subscribe(listener),
    () => downloadProgressStore.getVersion(),
    () => 0,
  );
}
